import { FC, useEffect, useMemo, useState } from 'react';

import { Autocomplete, TextField } from '@mui/material';
import { createFilterOptions } from '@mui/material/Autocomplete';

import { WarehouseOption, fetchWarehouseList, flattenWarehouseOptions } from '../model';

interface WarehouseSelectProps {
  value: string;
  onChange: (whId: string) => void;
  location: string;
  disabled?: boolean;
}

const warehouseFilter = createFilterOptions<WarehouseOption>({
  stringify: (option) => `${option.name} ${option.wh_id}`,
});

// Pittsburgh has 122 active warehouses, 90 of them technician vehicles, so this is
// an Autocomplete rather than a select: without type-ahead the receiver scrolls
// past 90 names to reach TUDI - Pittsburgh.
const WarehouseSelect: FC<WarehouseSelectProps> = ({ value, onChange, location, disabled }) => {
  const [options, setOptions] = useState<WarehouseOption[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [errorMessage, setErrorMessage] = useState<string>('');

  useEffect(() => {
    const fetchWarehouses = async () => {
      setLoading(true);
      try {
        const data = await fetchWarehouseList(location);
        setOptions(flattenWarehouseOptions(data));
        setErrorMessage('');
      } catch {
        setOptions([]);
        setErrorMessage('Could not load warehouses.');
      } finally {
        setLoading(false);
      }
    };

    fetchWarehouses();
  }, [location]);

  const selectedOption = useMemo(() => options.find((option) => option.wh_id === value) || null, [options, value]);

  return (
    <Autocomplete
      fullWidth
      disabled={disabled}
      loading={loading}
      options={options}
      value={selectedOption}
      filterOptions={warehouseFilter}
      groupBy={(option) => option.group || ''}
      getOptionLabel={(option) => option.name}
      isOptionEqualToValue={(option, selected) => option.wh_id === selected.wh_id}
      onChange={(_event, option) => onChange(option ? option.wh_id : '')}
      renderInput={(params) => (
        <TextField {...params} label="Warehouse" error={!!errorMessage} helperText={errorMessage} />
      )}
    />
  );
};

export default WarehouseSelect;
